import { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Session } from '@supabase/supabase-js';

export type UserRole = 'super_admin' | 'agency_owner' | 'owner' | 'agent' | 'finance' | 'viewer';

export interface AgencyProfile {
  id: string;
  name: string;
  logo: string;
  brandColor: string;
  timezone: string;
  currency: string;
  plan: string;
  country: string;
}

export interface TenantUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar: string;
}

interface TenantContextValue {
  currentUser: TenantUser;
  currentRole: UserRole;
  setCurrentRole: (role: UserRole) => void;
  agencyProfile: AgencyProfile;
  updateAgencyProfile: (updates: Partial<AgencyProfile>) => void;
  isSuperAdmin: boolean;
  session: Session | null;
  authLoading: boolean;
  mustChangePassword: boolean;
  clearMustChangePassword: () => Promise<void>;
  signOut: () => Promise<void>;
}

const TenantContext = createContext<TenantContextValue | null>(null);

const defaultAgency: AgencyProfile = {
  id: '',
  name: 'My Agency',
  logo: 'MA',
  brandColor: '#0D9488',
  timezone: 'Asia/Dubai',
  currency: 'AED',
  plan: 'starter',
  country: '',
};

const defaultUser: TenantUser = {
  id: '',
  name: 'User',
  email: '',
  role: 'agency_owner',
  avatar: 'U',
};

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentRole, setCurrentRoleState] = useState<UserRole>('agency_owner');
  const [agencyProfile, setAgencyProfile] = useState<AgencyProfile>(defaultAgency);
  const [currentUser, setCurrentUser] = useState<TenantUser>(defaultUser);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const loadTenantData = useCallback(async (userId: string) => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (!profile) return;

      const role = (profile.role as UserRole) || 'agency_owner';
      setCurrentRoleState(role);
      setCurrentUser({
        id: profile.id,
        name: profile.full_name || 'User',
        email: profile.email || '',
        role,
        avatar: (profile.full_name || 'U').slice(0, 2).toUpperCase(),
      });
      setMustChangePassword(profile.must_change_password === true);

      if (profile.tenant_id) {
        const { data: tenant } = await supabase
          .from('tenants')
          .select('*')
          .eq('id', profile.tenant_id)
          .maybeSingle();

        if (tenant) {
          setAgencyProfile({
            id: tenant.id,
            name: tenant.name,
            logo: tenant.name.slice(0, 2).toUpperCase(),
            brandColor: tenant.brand_color || '#0D9488',
            timezone: tenant.timezone || 'Asia/Dubai',
            currency: tenant.currency || 'AED',
            plan: tenant.plan || 'starter',
            country: tenant.country || '',
          });
        }
      }
    } catch (_e) {
      // fallback to defaults
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) loadTenantData(s.user.id);
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s?.user) loadTenantData(s.user.id);
      else {
        setCurrentUser(defaultUser);
        setAgencyProfile(defaultAgency);
        setCurrentRoleState('agency_owner');
      }
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [loadTenantData]);

  const setCurrentRole = (role: UserRole) => {
    setCurrentRoleState(role);
  };

  const updateAgencyProfile = async (updates: Partial<AgencyProfile>) => {
    setAgencyProfile((prev) => ({ ...prev, ...updates }));
    if (agencyProfile.id) {
      await supabase.from('tenants').update({
        name: updates.name,
        brand_color: updates.brandColor,
        timezone: updates.timezone,
        currency: updates.currency,
        country: updates.country,
      }).eq('id', agencyProfile.id);
    }
  };

  const clearMustChangePassword = async () => {
    setMustChangePassword(false);
    if (session?.user?.id) {
      await supabase
        .from('profiles')
        .update({ must_change_password: false })
        .eq('id', session.user.id);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value = useMemo(
    () => ({
      currentUser,
      currentRole,
      setCurrentRole,
      agencyProfile,
      updateAgencyProfile,
      isSuperAdmin: currentRole === 'super_admin',
      session,
      authLoading,
      mustChangePassword,
      clearMustChangePassword,
      signOut,
    }),
    [currentUser, currentRole, agencyProfile, session, authLoading, mustChangePassword]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside TenantProvider');
  return ctx;
}
